import React, { useState, useEffect } from 'react';
import Loading from '../../Common/Loading';

import ReactQuill, { Quill } from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';

/* ---------- Store styles inline (style="...") instead of ql-* classes ---------- */
const SIZES = ['12px', '14px', '18px', '24px', '32px', '48px'];

const AlignStyle = Quill.import('attributors/style/align') as any;
const FontStyle = Quill.import('attributors/style/font') as any;
const SizeStyle = Quill.import('attributors/style/size') as any;
SizeStyle.whitelist = SIZES;

Quill.register(AlignStyle, true);
Quill.register(FontStyle, true);
Quill.register(SizeStyle, true);

/* ---------- Convert legacy <font color size> tags (old VB data) to styled spans ---------- */
const FONT_SIZE_MAP: Record<string, string> = {
    '1': '12px', '2': '12px', '3': '14px', '4': '18px', '5': '24px', '6': '32px', '7': '32px'
};

const normalizeLegacyHtml = (html: string): string => {
    if (!html || !/<font/i.test(html)) return html;

    const doc = new DOMParser().parseFromString(html, 'text/html');
    doc.body.querySelectorAll('font').forEach((font) => {
        const span = doc.createElement('span');
        const color = font.getAttribute('color');
        const size = font.getAttribute('size');

        if (color) span.style.color = color;
        if (size && FONT_SIZE_MAP[size]) span.style.fontSize = FONT_SIZE_MAP[size];

        while (font.firstChild) span.appendChild(font.firstChild);
        font.replaceWith(span);
    });

    return doc.body.innerHTML;
};

const sizeLabelCss = SIZES.map(
    (s) => `
    .ql-snow .ql-picker.ql-size .ql-picker-label[data-value="${s}"]::before,
    .ql-snow .ql-picker.ql-size .ql-picker-item[data-value="${s}"]::before {
        content: '${s}';
    }`
).join('\n');

const EL_SetSiteMessage: React.FC = () => {
    const API_BASE_URL = process.env.REACT_APP_DOCUMENT_LINK;

    const [msgId, setMsgId] = useState<number | null>(null);
    const [messageText, setMessageText] = useState<string>('');
    const [status, setStatus] = useState<string>('I');
    const [loading, setLoading] = useState<boolean>(false);

    document.body.style.backgroundColor = "#fff";
    document.body.style.fontFamily = "Times New Roman";

    const editorModules = {
        toolbar: [
            ['bold', 'italic', 'underline'],
            [{ 'font': [] }, { 'size': SIZES }],
            [{ 'color': [] }, { 'background': [] }],
            [{ 'align': [] }],
            [{ 'list': 'ordered' }, { 'list': 'bullet' }],
            [{ 'indent': '-1' }, { 'indent': '+1' }],
            ['clean']
        ],
    };

    useEffect(() => {
        document.body.style.margin = '0px';
        document.body.style.fontFamily = 'Arial, sans-serif';
        fetchLatestMessage();
    }, []);

    const fetchLatestMessage = async () => {
        setLoading(true);
        try {
            const res = await fetch(`${API_BASE_URL}api/site_message/latest`);
            const json = await res.json();
            if (json.error) throw new Error(json.error);

            if (json.id) {
                setMsgId(json.id);
                setMessageText(normalizeLegacyHtml(json.message));
                setStatus(json.status);
            }
        } catch (err) {
            console.error(err);
            alert("Error loading site message.");
        } finally {
            setLoading(false);
        }
    };

    const handleSave = async () => {
        const plainText = messageText.replace(/(<([^>]+)>)/gi, "").replace(/&nbsp;/ig, '').trim();

        if (!plainText) {
            alert('Enter Message');
            return;
        }

        setLoading(true);
        try {
            const res = await fetch(`${API_BASE_URL}api/site_message/save`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    id: msgId,
                    message: messageText, // HTML with inline styles
                    status: status
                })
            });

            const json = await res.json();

            if (json.error) {
                alert(json.error);
            } else {
                alert(json.message);
                fetchLatestMessage();
            }

        } catch (err) {
            alert("Error saving site message.");
        } finally {
            setLoading(false);
        }
    };

    const handleNewMessage = async () => {
        setLoading(true);
        try {
            const res = await fetch(`${API_BASE_URL}api/site_message/new`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' }
            });

            const json = await res.json();

            if (json.error) {
                alert(json.error);
            } else {
                fetchLatestMessage();
            }

        } catch (err) {
            alert("Error creating new message.");
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        window.location.href = "/SysAdmin"
    };

    const isNewMessageDisabled = status === 'A';

    return (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginTop: '20px' }}>

            {/* Inject editor styling */}
            <style>
                {`
                .black-editor .ql-editor {
                    color: black !important;
                    font-size:"12px" !important;
                    font-family: "Times New Roman", serif;
                    padding:"5px !important";
                    border:"none !important";
                }

                .black-editor .ql-editor p {
                    color: black !important;
                    font-size:"12px";
                    font-family: "Times New Roman", serif;
                    font-weight: normal !important;
                
                }
                .ql-toolbar.ql-snow {
                    border-bottom: 1px solid blue !important;
                    background-color: #f0f0f0 !important;
                    padding: 1px !important;
                    border:"none !important";
                }
                .ql-container.ql-snow {
                    border:"1px solid blue !important";
                    }

                .black-editor .ql-container.ql-snow {
                    border: none !important;}
                    .ql-editor{
                    padding: 5px !important;}

                ${sizeLabelCss}
                `}
            </style>

            {loading && <Loading />}

            <table style={{ width: '800px', borderCollapse: 'collapse' }}>
                <tbody>
                    <tr>
                        <td style={{ fontSize: '12pt', color: 'white', backgroundColor: 'teal', padding: '8px', textAlign: 'center', fontWeight: 'bold' }}>
                            Adding/Editing site message
                        </td>
                    </tr>
                </tbody>
            </table>

            <br />

            <table style={{ width: '780px', borderCollapse: 'collapse' }}>
                <tbody>
                    <tr>
                        <td style={{ width: '600px', textDecoration: 'underline' }}>Latest Message</td>
                        <td style={{ width: '100px', textAlign: 'right' }}>
                            <button
                                onClick={handleNewMessage}
                                disabled={isNewMessageDisabled}
                                style={{ padding: '4px 10px', cursor: isNewMessageDisabled ? 'default' : 'pointer' }}
                            >
                                New Message
                            </button>
                        </td>
                    </tr>
                </tbody>
            </table>

            <table style={{ textAlign: 'left', width: '780px', marginTop: '3px' }}>
                <tbody>
                    <tr>
                        <td style={{ width: '800px', textAlign: 'left', padding: '3px 0' }}>
                            <div style={{ border: '0.8px solid blue', backgroundColor: '#fff' }}>
                                <ReactQuill
                                    theme="snow"
                                    value={messageText}
                                    onChange={setMessageText}
                                    modules={editorModules}
                                    className="black-editor"
                                    style={{ height: '200px', marginBottom: '30px', fontWeight: "normal" }}
                                />
                            </div>
                        </td>
                    </tr>
                </tbody>
            </table>

            <table style={{ width: '780px', borderCollapse: 'collapse', marginTop: '10px' }}>
                <tbody>
                    <tr>
                        <td style={{ width: '60px', fontWeight: 'bold' }}>Status :</td>

                        <td style={{ width: '80px' }}>
                            <label>
                                <input
                                    type="radio"
                                    checked={status === 'A'}
                                    onChange={() => setStatus('A')}
                                    style={{ marginTop: "-2px" }}
                                /> Active
                            </label>
                        </td>

                        <td style={{ width: '260px' }}>
                            <label>
                                <input
                                    type="radio"
                                    checked={status === 'I'}
                                    onChange={() => setStatus('I')}
                                    style={{ marginTop: "-2px" }}
                                /> Inactive
                            </label>
                        </td>

                        <td style={{ width: '100px', textAlign: 'right' }}>
                            <button onClick={handleSave} style={{ width: '110px', padding: '2px' }}>
                                Save
                            </button>
                        </td>

                        <td style={{ width: '100px', textAlign: 'right' }}>
                            <button onClick={handleClose} style={{ width: '110px', padding: '2px' }}>
                                Close
                            </button>
                        </td>

                    </tr>
                </tbody>
            </table>

        </div>
    );
};

export default EL_SetSiteMessage;